import { Box } from "@material-ui/core";
import { styled } from "@mui/material";
import { ToastContainer } from "@volt_developers/components";
import { SnackbarProvider } from "notistack";
import { ReactNode, useRef } from "react";
import { createGlobalStyle } from "styled-components";

import { Notifier } from "components/Notifier";
import { PAGE_HEIGHT } from "constants/styles";
import { HelpHeaderProvider } from "context/HelpHeaderContext";
import {
  BreadcrumbsProvider,
  Header,
  Sidebar,
  SIDEBAR_WIDTH,
} from "modules/common";
import { shouldForwardPropHelper } from "utils/shouldForwardPropHelper";

import { useApplicationShell } from "../hooks/useApplicationShell";

import type { theme as voltTheme } from "@volt_developers/react-ui-components";

const SNACK_NUMBER = 3;
const DURATION_IN_MS = 3000;

export interface DashboardProps {
  children?: ReactNode;
}

function Dashboard({ children }: DashboardProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const {
    toggleSidebar,
    isSidebarOpen,
    handleTransitionEnd,
    isSidebarHidden,
  } = useApplicationShell();

  return (
    <NewRoot>
      <ToastContainer />
      <SnackbarProvider
        maxSnack={SNACK_NUMBER}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        autoHideDuration={DURATION_IN_MS}
      >
        <HelpHeaderProvider>
          <BreadcrumbsProvider>
            <Notifier />
            <GlobalStyle />
            <AppContainer
              ref={rootRef}
              isOpen={isSidebarOpen}
              onTransitionEnd={handleTransitionEnd}
            >
              <Sidebar isHidden={isSidebarHidden} />
              <MainWrapper>
                <Header onMenuClick={toggleSidebar} />
                <Box display="flex" flexDirection="column" flex={1}>
                  <ContentWrapper>{children}</ContentWrapper>
                </Box>
              </MainWrapper>
            </AppContainer>
          </BreadcrumbsProvider>
        </HelpHeaderProvider>
      </SnackbarProvider>
    </NewRoot>
  );
}

const GlobalStyle = createGlobalStyle<{ theme: typeof voltTheme }>`
  html,
  body,
  #root {
    height: 100%;
  }

  *, ::after, ::before {
    box-sizing: inherit;
  }
`;

const NewRoot = styled("div")({
  minHeight: "100vh",
  boxSizing: "border-box",
});

interface AppContainerProps {
  isOpen: boolean;
}

const APP_CONTAINER_PROPS = new Set(["isOpen"]);

const AppContainer = styled("div", {
  shouldForwardProp: (prop) =>
    shouldForwardPropHelper(prop, APP_CONTAINER_PROPS),
})<AppContainerProps>(({ isOpen }) => ({
  display: "grid",
  gridTemplateColumns: "0px 100%",
  transition: "grid-template-columns 0.3s, opacity 0.3s",
  // Required to trigger onTransitionEnd on Safari as it doesn't work for grid-template-columns
  opacity: 0.9,

  ...(isOpen && {
    gridTemplateColumns: `${SIDEBAR_WIDTH}px calc(100% - ${SIDEBAR_WIDTH}px)`,
    opacity: 1,
  }),
}));

const ContentWrapper = styled("div")(({ theme }) => ({
  height: PAGE_HEIGHT,
  backgroundColor: theme.palette.ui10,
  overflow: "auto",
}));

const MainWrapper = styled("div")({
  minWidth: "450px",
});

export default Dashboard;
